import { Suspense, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { MdDeleteForever } from "react-icons/md";
import { RiEditFill } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import ToolTip from "../../../Components/CustomComponents/ToolTip";
import DetailQuestionModal from "../../../Components/Modals/DetailQuestionModal";
// import { useGetAllCategoryQuery } from "../../../Data/services/categoryApi";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Toast, { SuccessToast } from "../../../Hooks/useToast";
import { apiClient } from "../../../lib/apiClient";
import { activityService } from "../../../Services/activityService";
import { categoryService } from "../../../Services/categoryService";

export const useActivityManagement = () => {
  const { control, watch } = useForm({
    defaultValues: {
      category_id: "",
    },
  });
  const category_id = watch("category_id");
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [indicationMessage, setIndicationMessage] = useState("loading...");
  const [rowData, setRowData] = useState([]);
  const [editedData, setEditedData] = useState("");
  const [deleteModal, setDeleteModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [dataFetched, setDataFetched] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilterData, setCategoryFilterData] = useState([]);

  // const { data: categories } = useQuery({
  //   queryKey: ["categories"],
  //   queryFn: () => apiClient.get("activity-categories?is_paginate=false"),
  // });

  const { data: categories } = categoryService.useList(
    {
      is_paginate: false,
    },
    {
      staleTime: 0, // always fresh
      refetchOnMount: true,
    }
  );

  useEffect(() => {
    if (categories) {
      let categoryOptions = [
        {
          value: "all",
          label: "All",
        },
      ];
      categories.data.forEach((cat) => {
        categoryOptions.push({
          value: cat.id,
          label: cat.name,
        });
      });
      setCategoryFilterData(categoryOptions);
    }
  }, [categories]);

  const { data, isLoading, isFetching, refetch } = activityService.useList(
    {
      search: searchQuery.length >= 3 ? searchQuery : "",
      limit: pageSize,
      page: currentPage,
      category_id:
        category_id === "all" || category_id === undefined ? "" : category_id,
    },
    {
      staleTime: 1000 * 5, // 5 seconds
    }
  );

  useEffect(() => {
    if (data) {
      setPageSize(data.links.per_page);
      setCurrentPage(data.links.current);
      setTotalRecords(data.links.total_records);
      setTotalPages(data.links.total);
      setRowData(data.data);
      setDataFetched(true);
      if (data.data.length === 0) {
        setIndicationMessage("No data found");
      }
    }
  }, [data]);

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
  };

  const handleViewModalOpen = () => setViewModal(true);
  const handleViewModalClose = () => {
    setViewModal(false);
    setEditedData("");
  };

  const handleEditModalOpen = () => setEditModal(true);
  const handleEditModalClose = () => {
    setEditModal(false);
    setEditedData("");
  };

  const handleDeleteModalOpen = () => setDeleteModal(true);
  const handleDeleteModalClose = () => {
    setDeleteModal(false);
    setEditedData("");
  };

  const handleAddCategory = () => {
    setEditedData(null);
    handleEditModalOpen();
  };

  const { mutate: deleteActivity, isPending: isDeleting } =
    activityService.useDelete();

  const deleteData = async (id) => {
    deleteActivity(id, {
      onSuccess: (data) => {
        if (data.code === 200) {
          handleDeleteModalClose();
          refetch();
          SuccessToast(data.message);
        }
      },
      onError: (error) => {
        Toast(error.message, "error");
      },
    });
  };

  function indication() {
    return indicationMessage;
  }

  const columns = [
    {
      dataField: "name",
      text: "Activity name",
      headerAlign: "center",
      align: "center",
      headerStyle: { width: "200px" },
      formatter: (cellContent) => (
        <span className="text-wrap-custom">{cellContent}</span>
      ),
    },
    {
      dataField: "category_id",
      text: "Category",
      headerAlign: "center",
      align: "center",
      headerStyle: { width: "200px" },
      formatter: (cellContent) => {
        const catName =
          categoryFilterData.find((cat) => cat.value === cellContent)?.label ||
          "";
        return <span className="text-wrap-custom">{catName}</span>;
      },
    },
    {
      dataField: "spoon_count",
      text: "Spoon Count",
      headerAlign: "center",
      align: "center",
      headerStyle: { width: "100px" },
      formatter: (cellContent) => (
        <span className="text-wrap-custom text-black rounded-pill badge p-2 bg-info-subtle">
          {cellContent}
        </span>
      ),
    },
    {
      dataField: "actions",
      text: "Actions",
      headerAlign: "center",
      align: "center",
      headerStyle: { width: "160px" },
      formatter: (cellContent, row) => {
        if (row.slug || row.id) {
          return (
            <div className="d-flex justify-content-center align-items-center ">
              {/* <ToolTip placement={"top"} content={"View"}>
                <div
                  className={` d-flex justify-content-center align-items-center bg-success-hover rounded-3 p-2 mx-2 `}
                  role="button"
                  onClick={() => {
                    setEditedData(row);
                    handleViewModalOpen(true);
                  }}
                >
                  <span
                    className={`align-items-center d-flex justify-content-center `}
                  >
                    <MdRemoveRedEye />
                  </span>
                </div>
              </ToolTip> */}
              <ToolTip placement={"top"} content={"Edit"}>
                <div
                  className={` d-flex justify-content-center align-items-center bg-success-hover rounded-3 p-2 mx-2 `}
                  role="button"
                  onClick={() => {
                    setEditedData(row);
                    handleEditModalOpen(true);
                  }}
                >
                  <span
                    className={`align-items-center d-flex justify-content-center `}
                  >
                    <RiEditFill />
                  </span>
                </div>
              </ToolTip>
              <ToolTip placement={"top"} content={"Delete"}>
                <div
                  className="d-flex justify-content-center align-items-center bg-danger-hover rounded-3 p-2 mx-2 "
                  role="button"
                  onClick={() => {
                    setEditedData(row);
                    handleDeleteModalOpen();
                  }}
                >
                  <span className={`d-flex align-items-center`}>
                    <MdDeleteForever />
                  </span>
                </div>
              </ToolTip>
            </div>
          );
        }
      },
    },
  ];

  const Modals = () => (
    <>
      {viewModal && (
        <Suspense fallback={<Spinner animation="grow" size="sm" />}>
          <DetailQuestionModal
            handleClose={handleViewModalClose}
            data={editedData}
            categoryData={categoryFilterData}
          />
        </Suspense>
      )}
    </>
  );

  return {
    searchQuery,
    handleSearchChange,
    rowData,
    columns,
    indication,
    totalRecords,
    totalPages,
    pageSize,
    currentPage,
    setCurrentPage,
    setPageSize,
    refetch,
    dataFetched,
    isFetching,
    isLoading,
    Modals,
    handleAddCategory,
    filterData: categoryFilterData,
    control,
    deleteModal,
    handleDeleteModalClose,
    editedData,
    deleteData,
    isDeleting,
    editModal,
    handleEditModalClose,
    categoryFilterData,
  };
};
